import { useState, useEffect } from "react";
import {
  ModalBackdrop,
  ModalContainer,
  ModalDialog,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

interface RenameChatModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  chatId: string;
  currentName: string;
  onSuccess?: () => void;
}

export function RenameChatModal({
  isOpen,
  onOpenChange,
  chatId,
  currentName,
  onSuccess,
}: RenameChatModalProps) {
  const [newName, setNewName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();

  useEffect(() => {
    if (isOpen) {
      setNewName(currentName);
      setErrorMessage(""); // Clear errors when modal opens
    }
  }, [isOpen, currentName]);

  const handleRename = async () => {
    if (!adminClient) {
      logger.error("Admin client not found");
      setErrorMessage(t("Admin client not available"));

      return;
    }

    if (!newName.trim()) {
      setErrorMessage(t("Please enter a valid name"));

      return;
    }

    if (newName.trim() === currentName) {
      onOpenChange(false);

      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(""); // Clear previous errors

      await adminClient.chatHistories.renameChatHistory({
        id: chatId,
        bodyRenameChatHistory: {
          newSummary: newName.trim(),
        },
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error("Error renaming chat", error);
      setErrorMessage(t("Failed to rename chat. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalBackdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContainer size="md">
        <ModalDialog>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:!text-white">
                {t("Rename Chat")}
              </ModalHeader>
              <ModalBody>
                <label className="text-sm font-medium">{t("Chat Name")}</label>
                <Input
                  autoFocus
                  isRequired
                  className="focus:outline-none"
                  data-id="rename-chat-input"
                  errorMessage={errorMessage}
                  isInvalid={!!errorMessage}
                  placeholder={t("Enter new chat name")}
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    if (errorMessage) setErrorMessage(""); // Clear error when user types
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isLoading) {
                      handleRename();
                    }
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <Button
                  data-id="rename-chat-cancel"
                  isDisabled={isLoading}
                  variant="ghost"
                  onPress={handleClose}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  className="btn-brand-primary text-sm"
                  data-id="rename-chat-save-button"
                  isPending={isLoading}
                  onPress={handleRename}
                >
                  {t("Rename")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalDialog>
      </ModalContainer>
    </ModalBackdrop>
  );
}
